import React, { useState, useEffect } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import './SignIn.css';

const BACKEND_URL = process.env.REACT_APP_API_URL;

function SignIn({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const [googleConfigError, setGoogleConfigError] = useState(false);
  const [authProvider, setAuthProvider] = useState(undefined);
  const orgSlug = localStorage.getItem("orgSlug");
const loadProviderConfig = (provider) => {
  if (provider === "google") {
    fetch(`${BACKEND_URL}/api/config/google`, {
      headers: { "X-Org-Slug": orgSlug }
    })
      .then(r => {
        if (!r.ok) throw new Error(`Config fetch failed: ${r.status}`);
        return r.json();
      })
      .then(({ clientId }) => {
        if (!clientId) throw new Error("No clientId returned");
        setGoogleClientId(clientId);
      })
      .catch(err => {
        console.error("Failed to load Google config:", err);
        setGoogleConfigError(true);
      });

  } else if (provider === "microsoft") {
    import("@azure/msal-browser").then(({ PublicClientApplication }) => {
      fetch(`${BACKEND_URL}/api/config/azure`, {
        headers: { "X-Org-Slug": orgSlug }
      })
        .then(r => r.json())
        .then(async ({ clientId, tenantId }) => {
          const instance = new PublicClientApplication({
            auth: {
              clientId,
              authority: `https://login.microsoftonline.com/${tenantId}`,
              redirectUri: window.location.origin,
            },
            cache: { cacheLocation: "sessionStorage" },
          });
          await instance.initialize();
          setMsalInstance(instance);
        })
        .catch(err => console.error("Failed to load Azure config:", err));
    });
  }
};
  // Load provider + relevant config from DB on mount
useEffect(() => {
  const provider = localStorage.getItem("authProvider");

  if (provider) {
    setAuthProvider(provider);
    loadProviderConfig(provider);
  } else {
    // Fallback: ask the backend what provider this org uses
    fetch(`${BACKEND_URL}/api/config/auth-provider`, {
      headers: { "X-Org-Slug": orgSlug }
    })
      .then(r => r.json())
      .then(({ authProvider: p }) => {
        if (p) {
          localStorage.setItem("authProvider", p);
          setAuthProvider(p);
          loadProviderConfig(p);
        } else {
          setAuthProvider(null);
        }
      })
      .catch(() => setAuthProvider(null));
  }
}, [orgSlug]);

  const handleMicrosoftLogin = async () => {
    if (!msalInstance) return;
    try {
      setLoading(true);
      const clientId = msalInstance.getConfiguration().auth.clientId;
      const apiScope = `api://${clientId}/access_as_user`;
      const loginRequest = { scopes: ["openid", "profile", "email", apiScope] };
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const account = loginResponse.account;
      msalInstance.setActiveAccount(account);
      await msalInstance.acquireTokenSilent({ ...loginRequest, account });

      const backendResponse = await fetch(`${BACKEND_URL}/api/users/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Org-Slug": orgSlug,
        },
        body: JSON.stringify({ name: account.name, email: account.username }),
      });

      if (!backendResponse.ok) throw new Error(`Signin failed: ${backendResponse.status}`);
      const user = await backendResponse.json();
      localStorage.setItem("authProvider", "microsoft");
      onLogin({ ...user, authProvider: "microsoft" });
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/users/signin-google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Org-Slug": orgSlug,
        },
        body: JSON.stringify({ idToken: credentialResponse.credential }),
      });

      if (res.status === 401) throw new Error("Your Google account is not approved. Contact an admin.");
      if (!res.ok) throw new Error(`Google signin failed: ${res.status}`);

      const data = await res.json();
      localStorage.setItem("authProvider", "google");
      localStorage.setItem("authToken", data.token);
      onLogin({ ...data, authProvider: "google" });
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Still loading provider config
if (authProvider === undefined) return <div className="signin-container">Loading...</div>;
if (!authProvider) return <div className="signin-container">No auth provider configured.</div>;


  return (
    <div className="signin-container">

      {authProvider === "microsoft" && (
        <button
          className="signin-ms-btn"
          onClick={handleMicrosoftLogin}
          disabled={loading || !msalInstance}
        >
          {loading ? "Signing in..." : (
            <>
              <MicrosoftIcon />
              Sign in with Microsoft
            </>
          )}
        </button>
      )}

      {authProvider === "google" && googleClientId && (
        <GoogleOAuthProvider clientId={googleClientId}>
          <div className="signin-google-wrap">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => alert("Google sign-in failed")}
              useOneTap={false}
              theme="outline"
              size="large"
              width="100%"
            />
          </div>
        </GoogleOAuthProvider>
      )}

{authProvider === "google" && !googleClientId && (
  googleConfigError
    ? <div>Failed to load sign-in config. Please refresh.</div>
    : <div>Loading sign-in...</div>
)}

    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

export default SignIn;